import { FaMagnifyingGlass, type AppIcon } from '../ui/FaIcons';
import { NAV_ITEMS } from './navItems';

/**
 * Métadonnées d'en-tête par route : titre + sous-titre affichés dans le header global.
 * L'icône n'est PAS dupliquée ici — elle est dérivée de NAV_ITEMS (clé `to`) pour
 * rester l'unique source de vérité avec la sidebar.
 *
 * Indexé par chemin exact. Pour les sous-routes dynamiques (/dossiers/:id, /facturation/:id…)
 * la résolution se fait par préfixe le plus long (voir resolvePageMeta).
 */
export interface PageMeta {
  title: string;
  subtitle?: string;
}

export const PAGE_META: Record<string, PageMeta> = {
  '/dashboard': {
    title: 'Tableau de bord',
    subtitle: 'Vue d’ensemble de l’activité du cabinet.',
  },
  '/tickets': {
    title: 'Demandes',
    subtitle: 'Demandes entrantes depuis le site et les formulaires.',
  },
  '/tickets/archives': {
    title: 'Archives',
    subtitle: 'Demandes archivées — la suppression définitive est disponible depuis cette vue.',
  },
  '/dossiers': {
    title: 'Dossiers',
    subtitle: 'Pilotez vos dossiers juridiques et leurs échéances.',
  },
  '/dossiers/kanban': {
    title: 'Dossiers',
    subtitle: 'Suivez l’avancement des dossiers par stade procédural.',
  },
  '/dossiers/': {
    title: 'Dossier',
    subtitle: 'Détail du dossier.',
  },
  '/contacts': {
    title: 'Répertoire',
    subtitle: 'Clients, parties adverses, experts et confrères.',
  },
  '/contacts/': {
    title: 'Contact',
    subtitle: 'Fiche détaillée du contact.',
  },
  '/taches': {
    title: 'Tâches',
    subtitle: 'Gérez les tâches et échéances de vos dossiers — vues liste, kanban et calendrier.',
  },
  '/feuille-de-temps': {
    title: 'Feuille de temps',
    subtitle: 'Saisie des prestations et du temps passé facturable.',
  },
  '/calendrier': {
    title: 'Calendrier',
    subtitle: 'Échéances, audiences et rendez-vous du cabinet.',
  },
  '/gmail-inbox': {
    title: 'Boite Gmail',
    subtitle:
      'Mails à trier et mails non rattachés. Pour activer la synchronisation, connectez votre boîte Gmail dans Paramètres → Intégrations.',
  },
  '/e-barreau': {
    title: 'e-Barreau',
    subtitle: 'Accès au RPVA depuis CIME — ouverture dans un nouvel onglet.',
  },
  '/parapheur': {
    title: 'Mon parapheur',
    subtitle: 'Documents à valider et historique de vos circuits de signature.',
  },
  '/fichiers': {
    title: 'Fichiers',
    subtitle: 'Tous les documents de vos dossiers, prévisualisables en un clic.',
  },
  '/bibliotheque': {
    title: 'Bibliothèque',
    subtitle:
      'Modèles, jurisprudences, notes internes — fichiers du cabinet hors dossiers clients.',
  },
  '/facturation': {
    title: 'Facturation',
    subtitle:
      'Factures, devis et avoirs du cabinet.',
  },
  '/facturation/': {
    title: 'Document de facturation',
    subtitle: 'Détail de la facture ou du devis.',
  },
  '/profil': {
    title: 'Mon profil',
    subtitle: 'Vos informations personnelles et préférences.',
  },
  '/equipe/': {
    title: 'Profil collaborateur',
    subtitle: 'Bio, compétences et charge de travail.',
  },
  '/recherche': {
    title: 'Recherche',
    subtitle: 'Résultats dans tout le cabinet : dossiers, contacts, documents, tâches…',
  },
  '/notifications': {
    title: 'Notifications',
    subtitle: 'Vos alertes et notifications récentes.',
  },
  '/logs': {
    title: 'Journal d’activité',
    subtitle: 'Historique des actions significatives du cabinet.',
  },
  '/stats': {
    title: 'Statistiques du cabinet',
    subtitle: 'Indicateurs de performance et de rentabilité.',
  },
  '/design-system': {
    title: 'Design System',
    subtitle: 'Composants et styles de l’interface CIME.',
  },
  '/parametres': {
    title: 'Paramètres du cabinet',
    subtitle: 'Configuration du cabinet, équipe et intégrations.',
  },
  '/assistant-ia': {
    title: 'Assistant IA',
    subtitle: 'Assistant juridique IA (à venir).',
  },
};

/** Icône par route, dérivée de NAV_ITEMS (clé `to`) — préfixe le plus long. */
const NAV_ICON_BY_PATH: Record<string, AppIcon> = {
  ...Object.fromEntries(NAV_ITEMS.map((item) => [item.to, item.icon])),
  // Pages absentes de la sidebar.
  '/recherche': FaMagnifyingGlass,
};

interface ResolvedPageMeta extends PageMeta {
  icon?: AppIcon;
}

/**
 * Résout les métadonnées d'en-tête pour un chemin donné.
 * Stratégie : correspondance exacte d'abord, sinon préfixe le plus long
 * (gère /dossiers/abc → '/dossiers/', /parametres/equipe → '/parametres', …).
 * Renvoie null si aucune entrée ne correspond (le header retombe sur le breadcrumb seul).
 */
export function resolvePageMeta(pathname: string): ResolvedPageMeta | null {
  const meta = matchLongestPrefix(PAGE_META, pathname);
  if (!meta) return null;
  const icon = matchLongestPrefix(NAV_ICON_BY_PATH, pathname);
  return { ...meta, icon: icon ?? undefined };
}

function matchLongestPrefix<T>(table: Record<string, T>, pathname: string): T | null {
  // Correspondance exacte prioritaire.
  const exact = table[pathname];
  if (exact !== undefined) return exact;

  let best: { key: string; value: T } | null = null;
  for (const [key, value] of Object.entries(table)) {
    // Une clé se terminant par '/' matche tout sous-chemin ; sinon match exact uniquement
    // (déjà traité ci-dessus) ou segment-préfixe (ex. '/parametres' pour '/parametres/equipe').
    const isPrefix = key.endsWith('/')
      ? pathname.startsWith(key)
      : pathname === key || pathname.startsWith(key + '/');
    if (isPrefix && (best === null || key.length > best.key.length)) {
      best = { key, value };
    }
  }
  return best ? best.value : null;
}
